import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  CloudUpload as CloudUploadIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import dayjs, { Dayjs } from "dayjs";
import {
  CATEGORY_LABELS,
  TIPO_DISPLAY_NAMES,
  InvoiceCategory,
  InvoiceTipo,
} from "../types/invoice";
import { ApiService } from "../services/api";

interface CreateInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const ACCEPTED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
];
const MAX_UPLOAD_SIZE = 10 * 1024 * 1024;
const DEFAULT_NIF_ADQUIRENTE = "517189020";

const CreateInvoiceModal: React.FC<CreateInvoiceModalProps> = ({
  open,
  onClose,
  onCreated,
}) => {
  const [nifEmitente, setNifEmitente] = useState("");
  const [nifAdquirente, setNifAdquirente] = useState(DEFAULT_NIF_ADQUIRENTE);
  const [valor, setValor] = useState("");
  const [dataEmissao, setDataEmissao] = useState<Dayjs | null>(dayjs());
  const [categoria, setCategoria] = useState<InvoiceCategory | "">("");
  const [tipo, setTipo] = useState<InvoiceTipo | "">("");
  const [descricao, setDescricao] = useState("");
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const resetForm = () => {
    setNifEmitente("");
    setNifAdquirente(DEFAULT_NIF_ADQUIRENTE);
    setValor("");
    setDataEmissao(dayjs());
    setCategoria("");
    setTipo("");
    setDescricao("");
    setPhotoFile(null);
    setPhotoPreview(null);
    setError(null);
  };

  const handleClose = () => {
    if (!submitting) {
      resetForm();
      onClose();
    }
  };

  const handlePhotoSelect = (file: File) => {
    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      setError("Formato não suportado (use JPEG, PNG, GIF, WebP ou PDF)");
      return;
    }
    if (file.size > MAX_UPLOAD_SIZE) {
      setError("Ficheiro demasiado grande (máximo 10MB)");
      return;
    }
    setPhotoFile(file);
    setPhotoPreview(
      file.type === "application/pdf" ? "pdf" : URL.createObjectURL(file)
    );
    setError(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handlePhotoSelect(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.dataTransfer.types.includes("Files")) return;
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setDragOver(false);
  };

  const removePhoto = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const isValid =
    /^\d{9}$/.test(nifEmitente) &&
    parseFloat(valor) > 0 &&
    dataEmissao &&
    categoria !== "";

  const handleSubmit = async () => {
    if (!isValid || !dataEmissao) return;

    setSubmitting(true);
    setError(null);

    try {
      const invoiceData: Record<string, unknown> = {
        qr_data: "MANUAL_ENTRY",
        nif_emitente: nifEmitente,
        nif_adquirente: nifAdquirente || undefined,
        valor: parseFloat(valor),
        data_emissao: dataEmissao.format("YYYY-MM-DD"),
        categoria,
        tipo: tipo || undefined,
        descricao: descricao || undefined,
      };

      const response = await ApiService.createInvoice(invoiceData);
      const invoiceId = response.data?.id;

      if (photoFile && invoiceId) {
        try {
          await ApiService.uploadInvoicePhoto(invoiceId, photoFile);
        } catch {
          // Invoice created but photo failed - not critical
          console.error("Photo upload failed, invoice was created");
        }
      }

      resetForm();
      onClose();
      onCreated();
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Erro ao criar fatura";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Nova Fatura</DialogTitle>
      <DialogContent>
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
        >
          {error && <Alert severity="error">{error}</Alert>}

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="NIF Emitente"
              value={nifEmitente}
              onChange={(e) =>
                setNifEmitente(e.target.value.replace(/\D/g, "").slice(0, 9))
              }
              required
              fullWidth
              error={nifEmitente.length > 0 && !/^\d{9}$/.test(nifEmitente)}
              helperText={
                nifEmitente.length > 0 && !/^\d{9}$/.test(nifEmitente)
                  ? "NIF deve ter 9 dígitos"
                  : undefined
              }
            />
            <TextField
              label="NIF Adquirente"
              value={nifAdquirente}
              onChange={(e) =>
                setNifAdquirente(
                  e.target.value.replace(/\D/g, "").slice(0, 9)
                )
              }
              fullWidth
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Valor (EUR)"
              value={valor}
              onChange={(e) => {
                const v = e.target.value;
                if (/^\d*\.?\d{0,2}$/.test(v) || v === "") setValor(v);
              }}
              required
              fullWidth
              inputProps={{ inputMode: "decimal" }}
            />
            <DatePicker
              label="Data de Emissão"
              value={dataEmissao}
              onChange={setDataEmissao}
              maxDate={dayjs()}
              slotProps={{
                textField: { required: true, fullWidth: true },
              }}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Categoria"
              value={categoria}
              onChange={(e) =>
                setCategoria(e.target.value as InvoiceCategory)
              }
              select
              required
              fullWidth
            >
              {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Tipo"
              value={tipo}
              onChange={(e) => setTipo(e.target.value as InvoiceTipo)}
              select
              fullWidth
            >
              <MenuItem value="">
                <em>Nenhum</em>
              </MenuItem>
              {Object.entries(TIPO_DISPLAY_NAMES).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <TextField
            label="Descrição"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            multiline
            rows={2}
            fullWidth
          />

          {/* Photo drop zone */}
          <input
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
            hidden
            ref={fileInputRef}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handlePhotoSelect(file);
              e.target.value = "";
            }}
          />
          {photoPreview ? (
            <Box sx={{ position: "relative", textAlign: "center" }}>
              {photoPreview === "pdf" ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    py: 2,
                  }}
                >
                  <PdfIcon sx={{ fontSize: 40, color: "error.main" }} />
                  <Typography variant="body2" color="text.secondary">
                    {photoFile?.name}
                  </Typography>
                </Box>
              ) : (
                <Box
                  component="img"
                  src={photoPreview}
                  alt="Preview"
                  sx={{
                    maxHeight: 150,
                    maxWidth: "100%",
                    borderRadius: 1,
                    objectFit: "contain",
                  }}
                />
              )}
              <Button
                size="small"
                color="error"
                onClick={removePhoto}
                sx={{ mt: 0.5 }}
              >
                Remover ficheiro
              </Button>
            </Box>
          ) : (
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                py: 3,
                borderRadius: 1,
                border: "2px dashed",
                borderColor: dragOver ? "primary.main" : "divider",
                bgcolor: dragOver ? "primary.50" : "grey.50",
                cursor: "pointer",
                transition: "all 0.15s ease",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: "action.hover",
                },
              }}
            >
              <CloudUploadIcon
                sx={{
                  fontSize: 32,
                  color: dragOver ? "primary.main" : "text.disabled",
                }}
              />
              <Typography
                variant="body2"
                color={dragOver ? "primary.main" : "text.secondary"}
              >
                Arraste um ficheiro ou clique para selecionar
              </Typography>
              <Typography variant="caption" color="text.disabled">
                JPEG, PNG, GIF, WebP ou PDF (max 10MB)
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isValid || submitting}
        >
          {submitting ? <CircularProgress size={20} /> : "Criar Fatura"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateInvoiceModal;
